import React, {useEffect, useState, useCallback, forwardRef, useImperativeHandle} from "react";

import GoogleMapReact from "google-map-react";
import * as h3 from "h3-js";
import {useTranslation} from "react-i18next";
import {ShopOutlined} from "@ant-design/icons";
import {message} from "antd";

import apiKey from "../../_core/apiKey";
import {pickupColor} from "../../constants/constants";
import {resolutionOptions} from "../../constants/constants";
import {useH3Operations} from "../../hooks/useH3Operations";
import {MapContainer, Marker} from "./H3Map.styles";
import {H3MapTools} from "./components/H3MapTools";
import {Hexagon} from "./components/Hexagon";
import {OtherHexagonsPanel} from "./components/OtherHexagonsPanel";
import {H3MapConfig} from "./config";

export const H3Map = forwardRef(
    (
        {
            markers = [],
            markerIcon = <ShopOutlined />,
            markerColor = pickupColor,
            initialHexagons = [],
            otherHexagons = [],
            onHexagonSelect,
            defaultCenter = H3MapConfig.map.defaultCenter,
            defaultZoom = H3MapConfig.map.defaultZoom,
            onMarkerClick,
            isH3Enabled = H3MapConfig.features.isH3Enabled,
            style = {},
            initialCity = null,
            disabledResolutions = [],
            isPreview = H3MapConfig.features.isPreview,
            showOtherHexagonsPanel = H3MapConfig.features.showOtherHexagonsPanel,
            autoResolution = H3MapConfig.features.autoResolution,
            showToolsPanel = H3MapConfig.features.showToolsPanel,
            isActionsEnabled = H3MapConfig.features.isActionsEnabled,
            onlyH3Resolutions = H3MapConfig.features.onlyH3Resolutions,
            isToolsPanelVisibleOnInitial = H3MapConfig.features.isToolsPanelVisibleOnInitial,
            showHexagonTooltip = H3MapConfig.features.showHexagonTooltip
        },
        ref
    ) => {
        const {i18n} = useTranslation([]);
        const [mapInstance, setMapInstance] = useState(null);
        const [mapsApi, setMapsApi] = useState(null);
        const [isToolsPanelVisible, setIsToolsPanelVisible] = useState(
            isToolsPanelVisibleOnInitial
        );
        const [showOtherHexagons, setShowOtherHexagons] = useState(
            H3MapConfig.features.showOtherHexagons
        );
        const [filteredOtherHexagons, setFilteredOtherHexagons] = useState(otherHexagons);
        const isMapLoaded = Boolean(mapsApi && mapInstance);

        const {
            resolution,
            setResolution,
            selectedHexagons,
            markerToHexagonMap,
            toggleHexagonSelection,
            totalSelectedHexagons,
            showNeighborHexagons,
            clearPreviewHexagons,
            previewHexagons,
            setSelectedHexagons,
            hoverHexagon,
            setHoverHexagon
        } = useH3Operations({
            markers,
            initialHexagons,
            onHexagonSelect,
            autoResolution,
            isH3Enabled,
            defaultZoom,
            map: mapInstance,
            maps: mapsApi
        });

        const handleMapInitialized = useCallback(
            ({map, maps}) => {
                setMapInstance(map);
                setMapsApi(maps);

                if (initialCity) {
                    const geocoder = new maps.Geocoder();
                    geocoder.geocode(
                        {
                            address: `${initialCity}, Turkey`,
                            region: "tr"
                        },
                        (results, status) => {
                            if (status === "OK") {
                                const {lat, lng} = results[0].geometry.location;
                                map.setCenter({lat: lat(), lng: lng()});
                            } else {
                                console.error("Şehir bulunamadı:", status);
                            }
                        }
                    );
                } else if (markers.length > 0) {
                    const mapBounds = new maps.LatLngBounds();
                    markers.forEach(marker => {
                        const {location} = marker;
                        if (location?.latitude && location?.longitude) {
                            mapBounds.extend({
                                lat: location.latitude,
                                lng: location.longitude
                            });
                        }
                    });
                    if (!mapBounds.isEmpty()) {
                        map.fitBounds(mapBounds);
                    }
                }
            },
            [markers, initialCity]
        );

        const handleMouseMove = useCallback(
            e => {
                if (!isH3Enabled || isPreview) {
                    setHoverHexagon(null);
                    return;
                }

                if (disabledResolutions.includes(resolution)) {
                    setHoverHexagon(null);
                    return;
                }

                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const hexId = h3.latLngToCell(lat, lng, resolution);
                const hexagonData = initialHexagons.find(h => h.index === hexId);

                if (hexagonData?.isNeighbor) {
                    setHoverHexagon(null);
                    return;
                }

                if (!isActionsEnabled) {
                    const isHexagonInInitial = initialHexagons.some(hex => hex.index === hexId);
                    if (isHexagonInInitial) {
                        setHoverHexagon(hexId);
                    } else {
                        setHoverHexagon(null);
                    }
                } else {
                    setHoverHexagon(hexId);
                }
            },
            [
                isH3Enabled,
                isActionsEnabled,
                resolution,
                disabledResolutions,
                setHoverHexagon,
                initialHexagons
            ]
        );

        const handleMouseOut = useCallback(() => {
            setHoverHexagon(null);
        }, [setHoverHexagon]);

        useEffect(() => {
            const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

            if (!safeOtherHexagons.length || !initialHexagons) {
                setFilteredOtherHexagons([]);
                return;
            }

            const nonInitialHexagons = safeOtherHexagons.filter(
                otherHex => !initialHexagons.some(initialHex => initialHex.index === otherHex.index)
            );

            const uniqueIndexMap = new Map();
            nonInitialHexagons.forEach(hexagon => {
                if (!uniqueIndexMap.has(hexagon.index)) {
                    uniqueIndexMap.set(hexagon.index, hexagon);
                }
            });

            const uniqueHexagons = Array.from(uniqueIndexMap.values());
            setFilteredOtherHexagons(uniqueHexagons);
        }, [initialHexagons, JSON.stringify(otherHexagons)]);

        useEffect(() => {
            if (!mapInstance || !mapsApi || !initialCity) return;

            const geocoder = new mapsApi.Geocoder();
            geocoder.geocode(
                {
                    address: `${initialCity}, Turkey`,
                    region: "tr"
                },
                (results, status) => {
                    if (status === "OK") {
                        const {lat, lng} = results[0].geometry.location;
                        mapInstance.panTo({lat: lat(), lng: lng()});
                    } else {
                        console.error("Şehir bulunamadı:", status);
                    }
                }
            );
        }, [mapInstance, mapsApi, initialCity]);

        useEffect(() => {
            if (!mapInstance || !mapsApi) return;

            mapsApi.event.clearListeners(mapInstance, "mousemove");
            mapsApi.event.clearListeners(mapInstance, "mouseout");

            const mouseMoveListener = mapsApi.event.addListener(
                mapInstance,
                "mousemove",
                handleMouseMove
            );

            const mouseOutListener = mapsApi.event.addListener(
                mapInstance,
                "mouseout",
                handleMouseOut
            );

            return () => {
                mapsApi.event.removeListener(mouseMoveListener);
                mapsApi.event.removeListener(mouseOutListener);
            };
        }, [mapInstance, mapsApi, handleMouseMove, handleMouseOut]);

        useImperativeHandle(ref, () => ({
            panToCity: async cityName => {
                if (!mapInstance || !mapsApi) {
                    return;
                }

                try {
                    const geocoder = new mapsApi.Geocoder();
                    const result = await new Promise((resolve, reject) => {
                        geocoder.geocode(
                            {
                                address: `${cityName}, Turkey`,
                                region: "tr"
                            },
                            (results, status) => {
                                if (status === "OK") {
                                    resolve(results[0]);
                                } else {
                                    reject(new Error(`Geocoding failed: ${status}`));
                                }
                            }
                        );
                    });

                    const {lat, lng} = result.geometry.location;
                    mapInstance.panTo({lat: lat(), lng: lng()});
                } catch (error) {
                    console.error("City not found:", error);
                }
            }
        }));

        const handleHexagonClick = useCallback(
            hexagonIndex => {
                if (!mapInstance || !mapsApi) return;
                const [lat, lng] = h3.cellToLatLng(hexagonIndex);
                mapInstance.setCenter({lat, lng});
                mapInstance.setZoom(12);
            },
            [mapInstance, mapsApi]
        );

        const handleMarkerFocus = useCallback(
            marker => {
                if (
                    !mapInstance ||
                    !mapsApi ||
                    !marker?.location?.latitude ||
                    !marker?.location?.longitude
                )
                    return;

                mapInstance.setCenter({
                    lat: marker.location.latitude,
                    lng: marker.location.longitude
                });
                mapInstance.setZoom(15);
            },
            [mapInstance, mapsApi]
        );

        const handleH3Selection = useCallback(
            (lat, lng) => {
                if (!isH3Enabled || !isActionsEnabled) {
                    return;
                }

                if (disabledResolutions.includes(resolution)) {
                    message.warning(`${resolution}. seviyede seçim yapılamaz.`);
                    return;
                }

                const hexId = h3.latLngToCell(lat, lng, resolution);

                const isAlreadySelected = selectedHexagons[resolution]?.has(hexId);

                toggleHexagonSelection(lat, lng);

                const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

                if (isAlreadySelected) {
                    const originalHexagon = safeOtherHexagons.find(hex => hex.index === hexId);
                    if (originalHexagon) {
                        const hexagonWithProperties = {
                            ...originalHexagon,
                            zIndex: originalHexagon.zIndex || H3MapConfig.hexagons.OTHER.zIndex
                        };
                        setFilteredOtherHexagons(prev => [...prev, hexagonWithProperties]);
                    }
                } else {
                    setFilteredOtherHexagons(prev => prev.filter(hex => hex.index !== hexId));
                }

                if (isAlreadySelected) {
                    clearPreviewHexagons();
                } else {
                    showNeighborHexagons(hexId);
                }
            },
            [
                isH3Enabled,
                isActionsEnabled,
                disabledResolutions,
                resolution,
                toggleHexagonSelection,
                selectedHexagons,
                showNeighborHexagons,
                clearPreviewHexagons,
                initialHexagons,
                otherHexagons
            ]
        );

        const handleHexagonDelete = useCallback(
            (hex, resolution) => {
                setSelectedHexagons(prevSelected => {
                    const newState = {...prevSelected};
                    const currentResolutionSet = new Set(prevSelected[resolution] || []);

                    currentResolutionSet.delete(hex);

                    if (previewHexagons.length > 0) {
                        previewHexagons.forEach(hexId => {
                            currentResolutionSet.delete(hexId);
                        });
                    }

                    if (currentResolutionSet.size === 0) {
                        delete newState[resolution];
                    } else {
                        newState[resolution] = currentResolutionSet;
                    }

                    return newState;
                });

                const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

                const isHexagonInOriginalOtherHexagons = safeOtherHexagons.some(
                    item => item.index === hex
                );
                if (isHexagonInOriginalOtherHexagons) {
                    const hexagonToAdd = safeOtherHexagons.find(item => item.index === hex);
                    const hexagonWithProperties = {
                        ...hexagonToAdd,
                        zIndex: hexagonToAdd.zIndex || H3MapConfig.hexagons.OTHER.zIndex
                    };
                    setFilteredOtherHexagons(prev => [...prev, hexagonWithProperties]);
                }

                clearPreviewHexagons();
            },
            [clearPreviewHexagons, previewHexagons, otherHexagons]
        );

        function handleMapClick(event) {
            const {lat, lng} = event;
            handleH3Selection(lat, lng);
        }

        return (
            <MapContainer style={style}>
                <GoogleMapReact
                    onGoogleApiLoaded={handleMapInitialized}
                    onClick={handleMapClick}
                    center={defaultCenter}
                    bootstrapURLKeys={{
                        key: apiKey,
                        language: i18n.resolvedLanguage || "tr"
                    }}
                    yesIWantToUseGoogleMapApiInternals
                    defaultZoom={defaultZoom}
                    options={{
                        fullscreenControl: false,
                        gestureHandling: "greedy",
                        disableDoubleClickZoom: true,
                        draggableCursor: "pointer"
                    }}
                >
                    {markers.map((marker, index) => (
                        <Marker
                            key={`marker-${marker?._id || index}`}
                            size={20}
                            lat={marker?.location?.latitude}
                            lng={marker?.location?.longitude}
                            icon={markerIcon}
                            text={marker?.name}
                            isH3Enabled={isH3Enabled}
                            color={markerColor}
                            data-testid="mapMarker"
                            onClick={() => {
                                onMarkerClick?.(marker);
                                if (marker?.location?.latitude && marker?.location?.longitude) {
                                    handleH3Selection(
                                        marker.location.latitude,
                                        marker.location.longitude
                                    );
                                }
                            }}
                        />
                    ))}

                    {selectedHexagons &&
                        isMapLoaded &&
                        Object.entries(selectedHexagons).map(([resolution, hexagons]) =>
                            Array.from(hexagons).map(hexId => {
                                const hexagonData = initialHexagons.find(h => h.index === hexId);
                                const hexResolution = h3.getResolution(hexId);
                                const calculatedZIndex = 50 + hexResolution * 10;

                                return (
                                    <Hexagon
                                        key={hexId}
                                        hexagon={hexId}
                                        hoverHexagon={hoverHexagon}
                                        isSelected={true}
                                        isPreview={false}
                                        color={
                                            hexagonData?.color ||
                                            H3MapConfig.hexagons.SELECTED.color
                                        }
                                        setHoverHexagon={setHoverHexagon}
                                        opacity={
                                            hexagonData?.isNeighbor
                                                ? 0.5
                                                : H3MapConfig.hexagons.SELECTED.opacity
                                        }
                                        maps={mapsApi}
                                        map={mapInstance}
                                        showTooltip={showHexagonTooltip}
                                        zIndex={calculatedZIndex}
                                        tooltip={hexagonData?.tooltip}
                                    />
                                );
                            })
                        )}

                    {Array.isArray(previewHexagons) &&
                        isMapLoaded &&
                        previewHexagons.map(hexId => (
                            <Hexagon
                                key={hexId}
                                hexagon={hexId}
                                isPreview={true}
                                color={H3MapConfig.hexagons.PREVIEW.color}
                                opacity={H3MapConfig.hexagons.PREVIEW.opacity}
                                maps={mapsApi}
                                map={mapInstance}
                                hoverHexagon={hoverHexagon}
                                setHoverHexagon={setHoverHexagon}
                                zIndex={H3MapConfig.hexagons.PREVIEW.zIndex}
                                showTooltip={showHexagonTooltip}
                            />
                        ))}

                    {hoverHexagon && isMapLoaded && (
                        <Hexagon
                            key={`hover-${hoverHexagon}`}
                            hexagon={hoverHexagon}
                            isPreview={false}
                            isSelected={true}
                            color={H3MapConfig.hexagons.HOVER.color}
                            opacity={H3MapConfig.hexagons.HOVER.opacity}
                            maps={mapsApi}
                            map={mapInstance}
                            hoverHexagon={hoverHexagon}
                            setHoverHexagon={setHoverHexagon}
                            zIndex={H3MapConfig.hexagons.HOVER.zIndex}
                            showTooltip={showHexagonTooltip}
                        />
                    )}

                    {isMapLoaded &&
                        showOtherHexagons &&
                        filteredOtherHexagons &&
                        filteredOtherHexagons.length > 0 &&
                        filteredOtherHexagons.map((hexagon, idx) => {
                            const hexResolution = h3.getResolution(hexagon.index);
                            const calculatedZIndex = 50 + hexResolution * 10;

                            return (
                                <Hexagon
                                    key={`other-${hexagon.index}-${idx}`}
                                    hexagon={hexagon.index}
                                    isSelected={false}
                                    isPreview={false}
                                    maps={mapsApi}
                                    map={mapInstance}
                                    hoverHexagon={hoverHexagon}
                                    color={hexagon.color || H3MapConfig.hexagons.OTHER.color}
                                    opacity={hexagon.opacity || H3MapConfig.hexagons.OTHER.opacity}
                                    showTooltip={showHexagonTooltip}
                                    setHoverHexagon={setHoverHexagon}
                                    zIndex={calculatedZIndex}
                                    tooltip={hexagon.tooltip}
                                />
                            );
                        })}
                </GoogleMapReact>

                {isH3Enabled && showOtherHexagonsPanel && (
                    <OtherHexagonsPanel
                        showOtherHexagons={showOtherHexagons}
                        setShowOtherHexagons={setShowOtherHexagons}
                    />
                )}

                {isH3Enabled && showToolsPanel && (
                    <H3MapTools
                        resolution={resolution}
                        setResolution={setResolution}
                        resolutionOptions={resolutionOptions}
                        totalHexagons={totalSelectedHexagons}
                        hexagonsByResolution={selectedHexagons}
                        markerH3Indexes={markerToHexagonMap}
                        handleListItemClick={handleHexagonClick}
                        handleMarkerZoom={handleMarkerFocus}
                        markerColor={markerColor}
                        isVisible={isToolsPanelVisible}
                        setIsVisible={setIsToolsPanelVisible}
                        disabledResolutions={disabledResolutions}
                        hoverHexagon={hoverHexagon}
                        setHoverHexagon={setHoverHexagon}
                        onHexagonDelete={handleHexagonDelete}
                        isActionsEnabled={isActionsEnabled}
                        onlyH3Resolutions={onlyH3Resolutions}
                        showOtherHexagons={showOtherHexagons}
                        setShowOtherHexagons={setShowOtherHexagons}
                        otherHexagons={filteredOtherHexagons}
                    />
                )}
            </MapContainer>
        );
    }
);
